// tslint:disable-next-line: max-line-length
import { ExportLimitsDialogComponent } from '@admin/components/dialogs/export-limits-dialog/export-limits-dialog.component';
import { DataSource } from '@angular/cdk/table';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { DataObject } from '@shared/models/data-object.model';
import { EntityType } from '@shared/models/entity-enum';
import { IExportLimits } from '@shared/models/iuser.model';
import { AppStorageService, TOTAL_ENTITIES } from '@shared/services/app-storage.service';
import { DialogService } from '@shared/services/dialog.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { isWhitelistedExportEntity } from './export-entities.config';

class ExportLimitsDataSource implements DataSource<IExportLimits> {
  constructor(private subject: BehaviorSubject<IExportLimits[]>) {}

  public connect(): Observable<IExportLimits[]> {
    return this.subject.asObservable();
  }

  public disconnect() {
    /**
     * Nothing to be done on disconnect.
     */
  }
}

@Component({
  selector: 'miq-export-limits',
  templateUrl: './export-limits.component.html',
  styleUrls: ['./export-limits.component.scss'],
  standalone: false,
})
export class ExportLimitsComponent implements OnInit, OnChanges {
  @Input() exportLimits: IExportLimits[];
  @Input() isReadyOnly = false;
  @Input() entityDisplayInfo: DataObject;
  @Output() submitEvent: EventEmitter<IExportLimits>;

  defaultEntities: DataObject;
  dataSubject: BehaviorSubject<IExportLimits[]>;
  exportLimitsDataSource: ExportLimitsDataSource;
  displayedColumns = [
    EntityType.Availability,
    EntityType.LeaseComp,
    EntityType.SaleComp,
    EntityType.PortfolioSale,
    EntityType.Property,
    EntityType.Tim,
    EntityType.Company,
    EntityType.Contact,
    EntityType.Tenancy,
    EntityType.Brand,
    'stackingplan',
    'actions',
  ];

  constructor(
    private notificationService: DialogService,
    private appStorageService: AppStorageService
  ) {
    this.submitEvent = new EventEmitter();
    this.dataSubject = new BehaviorSubject([]);
    this.exportLimitsDataSource = new ExportLimitsDataSource(this.dataSubject);
    this.defaultEntities = ((this.appStorageService.getItem(TOTAL_ENTITIES) || []) as DataObject[]).reduce(
      (acc, { entityObj }) => ({ ...acc, [entityObj.name.toLowerCase()]: entityObj.isChecked }),
      { actions: true }
    );
  }

  public ngOnInit() {
    this.dataSubject.next(this.exportLimits);
  }

  public ngOnChanges(changes: SimpleChanges) {
    this.entityDisplayInfo ||= this.defaultEntities;
    this.displayedColumns = this.displayedColumns.filter(
      element => this.entityDisplayInfo[element] || isWhitelistedExportEntity(element)
    );
    if (!!changes.exportLimits) {
      // refresh table when this.exportLimits has changed.
      this.dataSubject.next(this.exportLimits);
    }
  }

  public openEditDialog() {
    this.openDialog(this.exportLimits[0])
      .pipe(filter(editedExportLimits => !!editedExportLimits))
      .subscribe(editedExportLimits => {
        /*
         * After emitting new export limits, component won't
         * refresh the table until this.editedExportLimits has
         * changed (received an ok from the database).
         */
        this.submitEvent.emit(editedExportLimits);
      });
  }

  /**
   * Open dialog and return dialog reference.
   *
   * @param exportLimits Optional record parameter to edit.
   * @return Dialog reference.
   */
  private openDialog(exportLimits: IExportLimits): Observable<IExportLimits> {
    return this.notificationService.openDialog(
      ExportLimitsDialogComponent,
      { exportLimits, entities: this.displayedColumns },
      {
        panelClass: 'table-modals',
      }
    );
  }
}
